// Centralized date and time formatting utilities

// Default formats - can be overridden by organization settings
const DEFAULT_FORMATS = {
  dateFormat: 'DD-MM-YYYY',
  timeFormat: '24h',
  dateTimeFormat: 'DD-MM-YYYY HH:mm',
  locale: 'en-GB'
};

// Get organization date/time settings from localStorage or use defaults
const getDateTimeSettings = () => {
  try {
    const organization = JSON.parse(localStorage.getItem('organization') || '{}');
    return {
      dateFormat: organization.dateFormat || DEFAULT_FORMATS.dateFormat,
      timeFormat: organization.timeFormat || DEFAULT_FORMATS.timeFormat,
      dateTimeFormat: organization.dateTimeFormat || DEFAULT_FORMATS.dateTimeFormat,
      locale: organization.locale || DEFAULT_FORMATS.locale
    };
  } catch {
    return DEFAULT_FORMATS;
  }
};

// Convert format string to toLocaleDateString options
const getDateOptions = (format, locale) => {
  const baseOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  };

  switch (format) {
    case 'MM-DD-YYYY':
      return { ...baseOptions, locale: 'en-US' };
    case 'DD-MM-YYYY':
      return { ...baseOptions, locale: 'en-GB' };
    case 'YYYY-MM-DD':
      return { ...baseOptions, locale: 'sv-SE' };
    case 'DD/MM/YYYY':
      return { ...baseOptions, locale: 'en-GB' };
    case 'MM/DD/YYYY':
      return { ...baseOptions, locale: 'en-US' };
    default:
      return { ...baseOptions, locale: locale || 'en-GB' };
  }
};

// Convert format string to toLocaleTimeString options
const getTimeOptions = (format) => {
  return {
    hour: '2-digit',
    minute: '2-digit',
    hour12: format === '12h'
  };
};

// Main formatting functions
export const formatDate = (date, customFormat = null) => {
  if (!date) return '';
  
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return '';
  
  const settings = getDateTimeSettings();
  const format = customFormat || settings.dateFormat;
  const options = getDateOptions(format, settings.locale);
  
  try {
    const formatted = dateObj.toLocaleDateString(options.locale, {
      day: options.day,
      month: options.month,
      year: options.year
    });
    
    // Convert to desired format
    switch (format) {
      case 'DD-MM-YYYY':
        return formatted.replace(/\//g, '-');
      case 'MM-DD-YYYY':
        return formatted.replace(/\//g, '-');
      case 'YYYY-MM-DD':
        return dateObj.toISOString().split('T')[0];
      case 'DD/MM/YYYY':
      case 'MM/DD/YYYY':
        return formatted;
      default:
        return formatted.replace(/\//g, '-');
    }
  } catch {
    return dateObj.toLocaleDateString();
  }
};

export const formatTime = (date, customFormat = null) => {
  if (!date) return '';
  
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return '';
  
  const settings = getDateTimeSettings();
  const format = customFormat || settings.timeFormat;
  const options = getTimeOptions(format);
  
  try {
    return dateObj.toLocaleTimeString(settings.locale, options);
  } catch {
    return dateObj.toLocaleTimeString();
  }
};

export const formatDateTime = (date, customFormat = null) => {
  if (!date) return '';
  
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return '';
  
  const settings = getDateTimeSettings();
  const format = customFormat || settings.dateTimeFormat;
  
  const datePart = formatDate(date);
  const timePart = formatTime(date);
  
  return `${datePart} ${timePart}`;
};

// Utility functions for common date operations
export const formatDateShort = (date) => {
  if (!date) return '';
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return '';
  
  const settings = getDateTimeSettings();
  try {
    return dateObj.toLocaleDateString(settings.locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).replace(/\s/g, '-');
  } catch {
    return dateObj.toLocaleDateString();
  }
};

export const formatDateLong = (date) => {
  if (!date) return '';
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return '';
  
  const settings = getDateTimeSettings();
  try {
    return dateObj.toLocaleDateString(settings.locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch {
    return dateObj.toLocaleDateString();
  }
};

// For input fields (always ISO format)
export const formatDateForInput = (date) => {
  if (!date) return '';
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return '';
  return dateObj.toISOString().split('T')[0];
};

export const formatDateTimeForInput = (date) => {
  if (!date) return '';
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return '';
  return dateObj.toISOString().slice(0, 16);
};

// Available format options for settings
export const DATE_FORMAT_OPTIONS = [
  { value: 'DD-MM-YYYY', label: 'DD-MM-YYYY (31-12-2024)' },
  { value: 'MM-DD-YYYY', label: 'MM-DD-YYYY (12-31-2024)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2024-12-31)' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/12/2024)' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2024)' }
];

export const TIME_FORMAT_OPTIONS = [
  { value: '24h', label: '24 Hour (14:30)' },
  { value: '12h', label: '12 Hour (2:30 PM)' }
];

export const LOCALE_OPTIONS = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'en-AU', label: 'English (Australia)' },
  { value: 'en-CA', label: 'English (Canada)' },
  { value: 'en-IN', label: 'English (India)' },
  { value: 'en-NZ', label: 'English (New Zealand)' },
  { value: 'en-SG', label: 'English (Singapore)' },
  { value: 'en-ZA', label: 'English (South Africa)' },
  { value: 'ar-SA', label: 'Arabic (Saudi Arabia)' },
  { value: 'ar-AE', label: 'Arabic (UAE)' },
  { value: 'zh-CN', label: 'Chinese (China)' },
  { value: 'zh-TW', label: 'Chinese (Taiwan)' },
  { value: 'nl-NL', label: 'Dutch (Netherlands)' },
  { value: 'fr-FR', label: 'French (France)' },
  { value: 'fr-CA', label: 'French (Canada)' },
  { value: 'de-DE', label: 'German (Germany)' },
  { value: 'it-IT', label: 'Italian (Italy)' },
  { value: 'ja-JP', label: 'Japanese (Japan)' },
  { value: 'ko-KR', label: 'Korean (South Korea)' },
  { value: 'pt-BR', label: 'Portuguese (Brazil)' },
  { value: 'pt-PT', label: 'Portuguese (Portugal)' },
  { value: 'ru-RU', label: 'Russian (Russia)' },
  { value: 'es-ES', label: 'Spanish (Spain)' },
  { value: 'es-MX', label: 'Spanish (Mexico)' },
  { value: 'es-AR', label: 'Spanish (Argentina)' },
  { value: 'sv-SE', label: 'Swedish (Sweden)' },
  { value: 'tr-TR', label: 'Turkish (Turkey)' }
];